import Phaser from 'phaser';
import { Robot } from '@/entities/Robot';
import { BehaviorRule, ActionType, ConditionType } from '@/behaviors/BehaviorDefs';
import { runBehaviorTick, BehaviorContext } from '@/behaviors/BehaviorEngine';
import { Enemy } from '@/entities/Enemy';
import { BASE_POSITIONS, MAP_GRID } from '@/map/MapData';
import { EventBus, GameEvents } from '@/events/EventBus';
import { ProjectileManager } from '@/systems/ProjectileManager';
import { MineManager } from '@/systems/MineManager';
import { updateRobotPhysics, resolveRobotCollisions } from '@/systems/RobotPhysics';
import { TILE_SIZE, ENEMY_DAMAGE_MULTIPLIER, EnemyType, TileType, GAME_WIDTH, GAME_HEIGHT } from '@/config/GameConfig';
import { GOD } from '@/config/GodMode';
import { ComponentType } from '@/components/ComponentDefs';
import { SoundManager, SfxKey } from '@/systems/SoundManager';
import { SpatialHash } from '@/systems/SpatialHash';
import { VFXPool } from '@/systems/ObjectPool';
import { ScoreManager } from '@/systems/ScoreManager';

// Powerful enemies that apply pushback + stun on melee lunge
// pushback = impulse velocity (px/s), stun = duration (seconds)
const ENEMY_MELEE_EFFECTS: Partial<Record<EnemyType, { pushback: number; stun: number }>> = {
  [EnemyType.TANK]:   { pushback: 120, stun: 0.6 },
  [EnemyType.BRUTE]:  { pushback: 200, stun: 1.0 },
  [EnemyType.BOMBER]: { pushback: 150, stun: 0.8 },
};

const ENEMY_ATK_SFX: Record<EnemyType, SfxKey> = {
  [EnemyType.GRUNT]: 'atkGrunt',
  [EnemyType.RUNNER]: 'atkRunner',
  [EnemyType.SWARM]: 'atkSwarm',
  [EnemyType.TANK]: 'atkTank',
  [EnemyType.BRUTE]: 'atkBrute',
  [EnemyType.PHANTOM]: 'atkPhantom',
  [EnemyType.DRONE]: 'atkDrone',
  [EnemyType.WASP]: 'atkWasp',
  [EnemyType.BOMBER]: 'atkBomber',
  [EnemyType.SPECTER]: 'atkSpecter',
  [EnemyType.HIVE]: 'atkBrute',
  [EnemyType.HEALER]: 'atkGrunt',
};

// ── Per-robot rule storage ──
const robotRules = new Map<string, BehaviorRule[]>();

export class RobotManager {
  private scene: Phaser.Scene;
  public robots: Robot[] = [];
  public totalDeployed: number = 0;
  public totalLost: number = 0;
  /** Spatial hash for fast robot proximity queries (used by melee + projectile checks) */
  public robotHash = new SpatialHash<Robot>(60);

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    // Track robot deaths
    EventBus.on(GameEvents.ROBOT_DESTROYED, () => { this.totalLost++; ScoreManager.recordRobotLost(); });
  }

  addRobot(robot: Robot, rules: BehaviorRule[]): void {
    this.robots.push(robot);
    this.totalDeployed++;

    // Auto-inject HARVEST rule for excavator-equipped robots if not already present
    const hasExcavator = robot.components.some(c => !c.broken && c.def.type === ComponentType.EXCAVATOR);
    const hasHarvestRule = rules.some(r => r.action === ActionType.HARVEST);
    if (hasExcavator && !hasHarvestRule) {
      // Insert as highest-priority movement rule (before PATROL) so excavators seek resources
      const minPriority = Math.min(...rules.map(r => r.priority), 1);
      rules.unshift({
        priority: minPriority,
        condition: ConditionType.RESOURCE_DETECTED,
        action: ActionType.HARVEST,
      });
    }

    robotRules.set(robot.id, rules);
    // Track unique designs + notify ScoreManager
    ScoreManager.recordDesign(robot.designName || robot.id);
    EventBus.emit(GameEvents.ROBOT_PLACED, robot);
  }

  removeRobot(robot: Robot): void {
    const idx = this.robots.indexOf(robot);
    if (idx !== -1) {
      this.robots.splice(idx, 1);
      robotRules.delete(robot.id);
    }
  }

  getRules(robot: Robot): BehaviorRule[] {
    return robotRules.get(robot.id) ?? [];
  }

  setRules(robot: Robot, rules: BehaviorRule[]): void {
    robotRules.set(robot.id, rules);
  }

  /** Update aggro targets BEFORE enemies move (call before waveManager.update) */
  updateAggroTargets(enemies: Enemy[], dt: number = 1 / 60): void {
    // Camouflaged robots are excluded from position lists and robot refs
    // so enemies never detect them (positions fallback has no isCamoActive)
    const groundRobotPositions = this.robots
      .filter(r => r.alive && !r.hasWings() && !r.isCamoActive)
      .map(r => ({ x: r.x, y: r.y }));
    const allRobotPositions = this.robots
      .filter(r => r.alive && !r.isCamoActive)
      .map(r => ({ x: r.x, y: r.y }));

    // Blocking robots (with stab legs)
    const blockers = this.robots.filter(r => r.alive && r.hasStabLegs());

    const groundRobots = this.robots.filter(r => r.alive && !r.hasWings());
    const allRobots = this.robots.filter(r => r.alive);

    for (const enemy of enemies) {
      if (!enemy.alive) continue;
      const positions = enemy.isFlyer ? allRobotPositions : groundRobotPositions;
      const robotRefs = enemy.isFlyer ? allRobots : groundRobots;
      enemy.updateAggroTarget(positions, robotRefs, dt);

      // Check for blocking robots (ground enemies only)
      if (!enemy.isFlyer) {
        let closestBlocker: Robot | null = null;
        let bestDist = TILE_SIZE * GOD.detectionRangeMult; // detection range
        for (const blocker of blockers) {
          const dx = blocker.x - enemy.body.x;
          const dy = blocker.y - enemy.body.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < bestDist) {
            bestDist = d;
            closestBlocker = blocker;
          }
        }
        if (closestBlocker) {
          const ref = closestBlocker;
          enemy.blockingRobot = {
            get x() { return ref.x; },
            get y() { return ref.y; },
            takeDamage: (d: number, ax?: number, ay?: number) => ref.takeDamage(d, ax, ay),
            get alive() { return ref.alive; },
          };
        } else {
          enemy.blockingRobot = null;
        }
      } else {
        enemy.blockingRobot = null;
      }
    }
  }

  update(enemies: Enemy[], delta: number, enemyHash?: SpatialHash<Enemy>): void {
    // Average base position for fallback target
    const baseX = BASE_POSITIONS.reduce((s, p) => s + p.x, 0) / BASE_POSITIONS.length;
    const baseY = BASE_POSITIONS.reduce((s, p) => s + p.y, 0) / BASE_POSITIONS.length;

    for (const robot of this.robots) {
      if (!robot.alive) continue;

      const rules = robotRules.get(robot.id) ?? [];
      // Get projectile manager from scene registry
      const pm = this.scene.registry.get('projectileManager') as ProjectileManager | undefined;
      const wm = this.scene.registry.get('waveManager') as { enemyBases?: import('@/entities/Actor').Actor[] } | undefined;
      const pb = this.scene.registry.get('playerBase') as import('@/entities/GameBase').PlayerBase | undefined;
      const mm = this.scene.registry.get('mineManager') as MineManager | undefined;
      const ct = this.scene.registry.get('crackedTiles') as import('@/systems/CrackedTileManager').CrackedTileManager | undefined;
      const ctx: BehaviorContext = {
        robot,
        enemies,
        allies: this.robots,
        delta,
        baseX,
        baseY,
        projectileManager: pm,
        scene: this.scene,
        enemyBases: wm?.enemyBases,
        playerBase: pb,
        enemyHash,
        mineManager: mm,
        crackedTiles: ct,
      };

      runBehaviorTick(rules, ctx);
    }

    // Physics: update velocity, position, friction, tilt for each robot
    for (const robot of this.robots) {
      if (!robot.alive) continue;
      updateRobotPhysics(robot, delta);
      robot.updateTopplePhysics(delta);
      robot.updateCamouflage(delta / 1000);
      robot.updatePoison(delta / 1000);
      robot.updateSpeedTrail(delta / 1000);
    }

    // Resolve robot-robot collisions
    resolveRobotCollisions(this.robots);

    // Enemy-enemy collision handled by WaveManager.resolveEnemyCollisions()

    // ── Enemy-robot collision + melee attack + contact damage ──
    // Rebuild robot spatial hash for this frame
    const aliveRobots = this.robots.filter(r => r.alive);
    this.robotHash.rebuild(aliveRobots, r => r.x, r => r.y);

    const MELEE_QUERY_R = 60; // max attack distance query radius
    for (const enemy of enemies) {
      if (!enemy.alive) continue;
      // Query only nearby robots instead of all robots
      const nearbyRobots = this.robotHash.queryRadius(enemy.body.x, enemy.body.y, MELEE_QUERY_R);
      for (const robot of nearbyRobots) {
        if (!robot.alive) continue;
        // Cloaked robots are invisible — enemies cannot melee them
        if (robot.isCamoActive) continue;
        const robotFlying = robot.hasWings();

        // Ground enemies cannot touch flying robots
        // Flying enemies can attack any robot
        if (!enemy.isFlyer && robotFlying) continue;

        const dx = enemy.body.x - robot.x;
        const dy = enemy.body.y - robot.y;
        const distSq = dx * dx + dy * dy;
        const gap = GOD.enemyRobotGap;
        const minDist = enemy.collisionRadius + robot.physicsRadius + gap;
        const attackDist = minDist + Enemy.MELEE_RANGE;

        // Early skip: if beyond attack range (squared), no interaction possible
        if (distSq > attackDist * attackDist) continue;

        const dist = Math.sqrt(distSq);

        // Push enemy away from robot if overlapping
        // Ground enemies get hard push; flyers get soft repulsion (no physical blocking)
        if (dist < minDist && dist > 0.01) {
          const nx = dx / dist;
          const ny = dy / dist;
          if (!enemy.isFlyer) {
            // Hard push: snap to minDist + extra 2px to prevent re-overlap next frame
            const pushDist = (minDist - dist) + 2;
            const newEx = enemy.body.x + nx * pushDist;
            const newEy = enemy.body.y + ny * pushDist;
            // Only push if target position is within bounds and walkable
            const margin = TILE_SIZE / 2;
            const cx = Math.max(margin, Math.min(GAME_WIDTH - margin, newEx));
            const cy = Math.max(margin, Math.min(GAME_HEIGHT - margin, newEy));
            const col = Math.floor(cx / TILE_SIZE);
            const row = Math.floor(cy / TILE_SIZE);
            const tile = MAP_GRID[row]?.[col];
            const walkable = tile === TileType.PATH || tile === TileType.PLACEMENT ||
              tile === TileType.BROKEN_PLACEMENT || tile === TileType.BASE ||
              tile === TileType.SPAWN || tile === TileType.MUD || tile === TileType.GRILLE;
            if (walkable) {
              enemy.body.setPosition(cx, cy);
            }
            enemy.inMeleeContact = true;
            enemy.markPathDirty();
          } else {
            // Flyers: soft repulsion push (proportional to overlap, no hard snap)
            const overlap = minDist - dist;
            const softPush = overlap * 0.6;
            enemy.body.setPosition(
              enemy.body.x + nx * softPush,
              enemy.body.y + ny * softPush,
            );
            enemy.inMeleeContact = true;
          }
        }

        // Melee lunge + contact damage when close enough (damage only on lunge hit)
        if (dist < attackDist) {
          const lunged = enemy.tryMeleeLunge(robot.x, robot.y);

          // Apply damage synced with lunge animation
          if (lunged) {
            robot.takeDamage(enemy.baseDamage * ENEMY_DAMAGE_MULTIPLIER, enemy.body.x, enemy.body.y);
            SoundManager.play(ENEMY_ATK_SFX[enemy.enemyType]);

            // Swarm applies slow debuff on melee hit
            if (enemy.enemyType === EnemyType.SWARM) {
              robot.applySlow(0.5, 2.0); // 50% speed for 2 seconds
            }

            // Pushback + stun from powerful enemies
            const meleeEffect = ENEMY_MELEE_EFFECTS[enemy.enemyType];
            if (meleeEffect) {
              robot.applyPushback(enemy.body.x, enemy.body.y, meleeEffect.pushback);
              robot.applyStun(meleeEffect.stun);
              // Stun visual: spinning stars around the robot
              if (VFXPool.ready) {
                for (let st = 0; st < 3; st++) {
                  const angle = (st / 3) * Math.PI * 2;
                  const starR = robot.physicsRadius + 8;
                  const star = VFXPool.acquireText(
                    robot.x + Math.cos(angle) * starR,
                    robot.y + Math.sin(angle) * starR,
                    '*', { fontSize: '14px', color: '#ffff44', fontFamily: 'monospace', fontStyle: 'bold' },
                  );
                  star.setOrigin(0.5).setDepth(26);
                  this.scene.tweens.add({
                    targets: star,
                    angle: 360,
                    alpha: 0,
                    duration: meleeEffect.stun * 1000,
                    onUpdate: () => {
                      const t = star.angle * Math.PI / 180;
                      star.setPosition(
                        robot.x + Math.cos(angle + t) * starR,
                        robot.y + Math.sin(angle + t) * starR,
                      );
                    },
                    onComplete: () => VFXPool.releaseText(star),
                  });
                }
              }
            }

            // Brief red flash overlay on the robot
            if (VFXPool.ready) {
              const flash = VFXPool.acquireCircle(robot.x, robot.y, robot.physicsRadius + 4, 0xff4444, 0.35);
              flash.setDepth(24);
              this.scene.tweens.add({
                targets: flash, alpha: 0, duration: 120,
                onComplete: () => VFXPool.releaseCircle(flash),
              });
              // Spark particle at impact point
              const impactX = robot.x + (enemy.body.x - robot.x) * 0.3;
              const impactY = robot.y + (enemy.body.y - robot.y) * 0.3;
              for (let s = 0; s < 3; s++) {
                const spark = VFXPool.acquireCircle(
                  impactX + (Math.random() - 0.5) * 8,
                  impactY + (Math.random() - 0.5) * 8,
                  2 + Math.random() * 2,
                  0xffaa44, 0.9,
                );
                spark.setDepth(25);
                this.scene.tweens.add({
                  targets: spark,
                  x: spark.x + (Math.random() - 0.5) * 16,
                  y: spark.y + (Math.random() - 0.5) * 16,
                  alpha: 0, scaleX: 0.2, scaleY: 0.2,
                  duration: 150 + Math.random() * 100,
                  onComplete: () => VFXPool.releaseCircle(spark),
                });
              }
            }
          }
        }
      }
    }

    // Cleanup destroyed robots
    for (let i = this.robots.length - 1; i >= 0; i--) {
      if (!this.robots[i].alive) {
        robotRules.delete(this.robots[i].id);
        this.robots.splice(i, 1);
      }
    }
  }

  shutdown(): void {
    robotRules.clear();
    this.robots = [];
    this.totalDeployed = 0;
    this.totalLost = 0;
    EventBus.off(GameEvents.ROBOT_DESTROYED);
  }
}
